import { buildSectionSelector } from '@theme/section-renderer';
import { getScrollContainer, getScrollTop, scrollTo } from '@theme/scroll-container';

const ALLOWED = new Set(['20', '50', '100']);
const VIEW_BY_SIZE = /** @type {const} */ ({ '20': null, '50': 'uucck-50', '100': 'uucck-100' });
const SIZE_BY_VIEW = /** @type {const} */ ({ 'uucck-50': '50', 'uucck-100': '100' });

const BAR_SELECTOR = '.uucck-collection-pagination-bar';
const PAGE_SIZE_BTN = '[data-uucck-page-size]';
const PAGINATION_LINK = `${BAR_SELECTOR} .pagination a.pagination__link[href]`;
const GRID_SELECTOR = 'ul.product-grid[data-testid="product-grid"]';
const GRID_ITEM_SELECTOR = '.product-grid .product-grid__item';

/** @type {Promise<void> | null} */
let pendingUpdate = null;

/**
 * @returns {HTMLElement | null}
 */
function getResultsList() {
  const el = document.querySelector('results-list[section-id]');
  return el instanceof HTMLElement ? el : null;
}

/**
 * @returns {string}
 */
function getActiveSizeFromUrl(url = new URL(window.location.href)) {
  const legacy = url.searchParams.get('page_size');
  if (legacy && ALLOWED.has(legacy)) return legacy;

  const view = url.searchParams.get('view');
  if (view && view in SIZE_BY_VIEW) return SIZE_BY_VIEW[/** @type {keyof typeof SIZE_BY_VIEW} */ (view)];

  return '20';
}

function applyActivePageSize(size) {
  if (!ALLOWED.has(size)) return;
  document.querySelectorAll(PAGE_SIZE_BTN).forEach((btn) => {
    if (!(btn instanceof HTMLButtonElement)) return;
    const on = btn.getAttribute('data-uucck-page-size') === size;
    btn.classList.toggle('is-active', on);
    btn.setAttribute('aria-pressed', on ? 'true' : 'false');
  });
  const root = document.querySelector('.uucck-collection-page-size');
  if (root) root.setAttribute('data-uucck-page-size-active', size);
}

function syncActivePageSizeFromUrl() {
  applyActivePageSize(getActiveSizeFromUrl());
}

function clearGridTransitionStyles() {
  document.querySelectorAll(GRID_ITEM_SELECTOR).forEach((node) => {
    if (!(node instanceof HTMLElement)) return;
    node.style.removeProperty('content-visibility');
    node.style.removeProperty('view-transition-name');
  });
}

function maxScrollTop() {
  const container = getScrollContainer();
  return Math.max(0, container.scrollHeight - container.clientHeight);
}

function clampScrollPosition() {
  scrollTo({ top: Math.min(getScrollTop(), maxScrollTop()), behavior: 'instant' });
}

/**
 * @param {URL} url
 */
function verifyListingMeta(url) {
  const requested = getActiveSizeFromUrl(url);
  const meta = document.querySelector('.uucck-collection-listing-meta');
  const effective = meta?.getAttribute('data-uucck-effective-page-size');
  if (effective && effective !== requested) {
    window.location.replace(url.toString());
  }
}

/**
 * Full collection HTML fetch so Liquid sees `?view=` (Section API ignores custom page_size).
 *
 * @param {string} sectionId
 * @param {URL} url
 */
async function swapListingFromFullPage(sectionId, url) {
  const pageUrl = new URL(url.toString());
  pageUrl.searchParams.delete('section_id');
  pageUrl.hash = '';

  const response = await fetch(pageUrl.toString(), {
    credentials: 'same-origin',
    headers: {
      Accept: 'text/html',
      'X-Requested-With': 'XMLHttpRequest',
    },
  });

  if (!response.ok) {
    window.location.assign(pageUrl.toString());
    return;
  }

  const html = await response.text();
  const parsed = new DOMParser().parseFromString(html, 'text/html');
  const sectionSelector = buildSectionSelector(sectionId);
  const newSection = parsed.querySelector(sectionSelector);
  const liveSection = document.querySelector(sectionSelector);

  if (!newSection || !liveSection) {
    window.location.assign(pageUrl.toString());
    return;
  }

  const newGrid = newSection.querySelector(GRID_SELECTOR);
  const liveGrid = liveSection.querySelector(GRID_SELECTOR);
  if (newGrid && liveGrid) {
    liveGrid.replaceWith(newGrid);
  }

  const newBar = newSection.querySelector(BAR_SELECTOR);
  const liveBar = liveSection.querySelector(BAR_SELECTOR);
  if (newBar && liveBar) {
    liveBar.replaceWith(newBar);
  } else if (newBar && !liveBar) {
    liveSection.querySelector('.product-grid-container')?.appendChild(newBar);
  }

  verifyListingMeta(pageUrl);
}

/**
 * @param {'page-size' | 'pagination'} reason
 */
function finalizeListingUpdate(reason) {
  clearGridTransitionStyles();

  const list = getResultsList();
  if (list && typeof list.updatedCallback === 'function') {
    list.updatedCallback();
  }

  requestAnimationFrame(() => {
    if (reason === 'page-size') {
      clampScrollPosition();
    }
    syncActivePageSizeFromUrl();
    document.dispatchEvent(new CustomEvent('uucck:collection-listing-updated'));
  });
}

/**
 * @param {URL} url
 * @param {{ updateHistory?: boolean; reason?: 'page-size' | 'pagination' }} [options]
 */
async function updateCollectionListing(url, options = {}) {
  const { updateHistory = true, reason = 'pagination' } = options;
  const list = getResultsList();
  const sectionId = list?.getAttribute('section-id');

  if (!sectionId) {
    window.location.assign(url.toString());
    return;
  }

  const scrollTop = getScrollTop();
  const bar = document.querySelector(BAR_SELECTOR);
  bar?.setAttribute('aria-busy', 'true');

  if (updateHistory) {
    history.pushState({ uucckCollectionListing: true }, '', url.toString());
  }

  try {
    await swapListingFromFullPage(sectionId, url);
  } finally {
    bar?.removeAttribute('aria-busy');
    if (reason === 'pagination') {
      requestAnimationFrame(() => {
        scrollTo({ top: Math.min(scrollTop, maxScrollTop()), behavior: 'instant' });
      });
    }
    finalizeListingUpdate(reason);
  }
}

/**
 * @param {string} size
 */
function buildPageSizeUrl(size) {
  const url = new URL(window.location.href);
  url.searchParams.delete('page_size');
  url.searchParams.delete('page');
  url.searchParams.delete('view');

  if (size !== '20' && VIEW_BY_SIZE[/** @type {'20'|'50'|'100'} */ (size)]) {
    url.searchParams.set('view', VIEW_BY_SIZE[/** @type {'50'|'100'} */ (size)]);
  }

  url.hash = '';
  return url;
}

/**
 * @returns {boolean}
 */
function migrateLegacyPageSizeParam() {
  const url = new URL(window.location.href);
  const legacy = url.searchParams.get('page_size');
  if (!legacy || !ALLOWED.has(legacy)) return false;

  const next = buildPageSizeUrl(legacy);
  history.replaceState({}, '', next.toString());
  return true;
}

/**
 * @param {Event} event
 */
function onPageSizeClick(event) {
  const btn = event.target instanceof Element ? event.target.closest(PAGE_SIZE_BTN) : null;
  if (!(btn instanceof HTMLButtonElement)) return;

  event.preventDefault();
  const size = btn.getAttribute('data-uucck-page-size');
  if (!size || !ALLOWED.has(size)) return;
  if (btn.classList.contains('is-active')) return;

  const url = buildPageSizeUrl(size);
  if (pendingUpdate) return;
  pendingUpdate = updateCollectionListing(url, { reason: 'page-size' }).finally(() => {
    pendingUpdate = null;
  });
}

/**
 * @param {Event} event
 */
function onPaginationClick(event) {
  const link = event.target instanceof Element ? event.target.closest(PAGINATION_LINK) : null;
  if (!(link instanceof HTMLAnchorElement)) return;
  if (link.classList.contains('pagination__link--disabled')) return;

  event.preventDefault();
  const url = new URL(link.href, window.location.origin);

  if (pendingUpdate) return;
  pendingUpdate = updateCollectionListing(url, { reason: 'pagination' }).finally(() => {
    pendingUpdate = null;
  });
}

function fixInvalidPageParam() {
  const url = new URL(window.location.href);
  const page = parseInt(url.searchParams.get('page') || '1', 10);
  const grid = document.querySelector('[data-last-page]');
  const lastPage = grid ? parseInt(grid.getAttribute('data-last-page') || '1', 10) : 1;
  if (page > lastPage && lastPage >= 1) {
    url.searchParams.delete('page');
    if (pendingUpdate) return;
    pendingUpdate = updateCollectionListing(url, { reason: 'page-size' }).finally(() => {
      pendingUpdate = null;
    });
  }
}

function init() {
  if (!document.body.classList.contains('template-collection')) return;

  const migrated = migrateLegacyPageSizeParam();
  syncActivePageSizeFromUrl();

  if (migrated) {
    pendingUpdate = updateCollectionListing(new URL(window.location.href), { reason: 'page-size' }).finally(() => {
      pendingUpdate = null;
    });
  } else {
    fixInvalidPageParam();
  }

  document.addEventListener('click', onPageSizeClick);
  document.addEventListener('click', onPaginationClick);

  window.addEventListener('popstate', () => {
    if (pendingUpdate) return;
    const url = new URL(window.location.href);
    pendingUpdate = updateCollectionListing(url, { updateHistory: false, reason: 'page-size' }).finally(() => {
      pendingUpdate = null;
    });
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
